/**
 * Navigate-path refusals — distinguish Workspace denial from the Worker's
 * already-generic receipt-context refusal (R18O-1).
 *
 * The Worker returns one public client string for every receipt-context refuse
 * (medical Zone-1, unconfigured hub, unreadable packs, missing row). Alert
 * `reason` stays ops-only. Matching that exact string does not widen the
 * oracle: any caller who can hit the endpoint already receives it. We only
 * stop replacing it with an untrue authz story.
 *
 * Do not invent a new error code here — that is a frozen-contract change.
 */
import { AdminApiError } from "./api.js";
import { failureCopy } from "./ui/failure.js";

/** Exact Worker client message from assertReceiptSeamNotMedical / hub gate. */
export const RECEIPT_CONTEXT_UNAVAILABLE_MESSAGE =
  "Company is not configured for receipt context";

/**
 * Operator-facing copy for that refusal. Still no medical / Zone-1 / alert
 * reason names — those stay in ops alerts.
 */
export const RECEIPT_CONTEXT_UNAVAILABLE_OPERATOR_COPY =
  "Receipt matching is not available for this company. " +
  "Pick another company, or go back — this is not an access problem.";

/**
 * The Worker's own words, scrubbed, for a caller that needs the raw message
 * rather than a face (rd-r12 B3a-2).
 *
 * It now reads through the one home of failure words (`ui/failure.ts`) instead
 * of holding a second `error.body.error.message` parser. `measured-claim.test.ts`
 * imports this name, so it stays exported; no page prints its result: a page
 * uses `failureCopy(...).sentence` on the face and `.detail` inside "Show
 * details".
 */
export function adminErrorBodyMessage(body: unknown): string | null {
  if (body === null || typeof body !== "object") return null;
  return failureCopy(new AdminApiError(0, body), {
    role: "owner",
    context: "write",
  }).detail;
}

/** True when a 403 body is the Worker's receipt-context refusal string. */
export function isReceiptContextRefusal(error: unknown): boolean {
  if (!(error instanceof AdminApiError) || error.status !== 403) return false;
  const body = error.body;
  if (body === null || typeof body !== "object") return false;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return false;
  // A comparison, not a sentence: the server's words are not shown here, only
  // asked whether they are the one public receipt-context string. The read is
  // inlined so it never becomes a value a face could carry.
  return (
    typeof (err as { message?: unknown }).message === "string" &&
    ((err as { message?: unknown }).message as string).trim() ===
      RECEIPT_CONTEXT_UNAVAILABLE_MESSAGE
  );
}
